import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum, subHeadingLevel } from "../globalProps";
import { IMAGE_SHAPE_CLASSES } from "../imageShapes";
import { SectionShell } from "../SectionShell";

const teamMemberSchema = z.object({
  name: z.string().default("Team Member"),
  title: z.string().default("Professional"),
  photoUrl: z.string().optional(),
  photoOriginalUrl: z.string().optional(),
  photoOriginalStorageKey: z.string().optional(),
  photoCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  bio: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Meet Our Team"),
  subheading: z.string().optional(),
  members: z
    .array(teamMemberSchema)
    .default([
      { name: "Alex Johnson", title: "Managing Partner", bio: "Over 15 years of experience helping clients navigate complex challenges with clarity and confidence." },
      { name: "Maria Chen", title: "Senior Advisor", bio: "Specializes in strategic planning and risk management for growing businesses." },
      { name: "David Park", title: "Associate", bio: "Focused on delivering thorough analysis and practical recommendations." },
      { name: "Rachel Torres", title: "Client Relations Manager", bio: "Ensures every client receives responsive, personalized attention." },
    ]),
  layout: z.enum(["grid", "featured"]).default("grid"),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(4),
  showBio: z.boolean().default(true),
  photoShape: z.enum(["square", "rounded", "circle"]).default("circle"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Initials placeholder ---------- */

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function PhotoPlaceholder({ name, large, shapeClass }: { name: string; large?: boolean; shapeClass: string }) {
  const size = large ? "w-48 h-48" : "w-24 h-24";
  const text = large ? "text-4xl" : "text-xl";
  return (
    <div
      className={`${size} ${shapeClass} bg-primary/15 text-primary flex items-center justify-center ${text} font-bold mx-auto`}
    >
      {getInitials(name)}
    </div>
  );
}

/* ---------- Member card (used in grid layout) ---------- */

function MemberCard({
  member,
  index,
  showBio,
  SubH,
  shapeClass,
}: {
  member: z.infer<typeof teamMemberSchema>;
  index: number;
  showBio: boolean;
  SubH: "h2" | "h3" | "h4";
  shapeClass: string;
}) {
  return (
    <div className="text-center">
      {member.photoUrl ? (
        <img
          src={member.photoUrl}
          alt={member.name}
          className={`w-24 h-24 ${shapeClass} object-cover mx-auto`}
          loading="lazy"
          data-item-prop={`members.${index}`}
        />
      ) : (
        <PhotoPlaceholder name={member.name} shapeClass={shapeClass} />
      )}
      <RichText
        as={SubH}
        html={member.name}
        className="text-lg font-semibold mt-4"
        data-prop={`members.${index}.name`}
      />
      <RichText
        as="p"
        html={member.title}
        className="text-sm opacity-70 mb-2"
        data-prop={`members.${index}.title`}
      />
      {showBio && member.bio && (
        <RichText
          as="p"
          html={member.bio}
          className="text-sm leading-relaxed opacity-80 line-clamp-3"
          data-prop={`members.${index}.bio`}
        />
      )}
    </div>
  );
}

/* ---------- Grid layout ---------- */

function GridLayout({ safe, SubH }: { safe: Props; SubH: "h2" | "h3" | "h4" }) {
  const colClass =
    safe.columns === 2
      ? "grid-cols-1 @md:grid-cols-2"
      : safe.columns === 3
        ? "grid-cols-1 @md:grid-cols-3"
        : "grid-cols-2 @md:grid-cols-4";
  const sc = IMAGE_SHAPE_CLASSES[safe.photoShape] || IMAGE_SHAPE_CLASSES.circle;

  return (
    <div className={`grid ${colClass} gap-10`}>
      {safe.members.map((member, i) => (
        <MemberCard key={i} member={member} index={i} showBio={safe.showBio} SubH={SubH} shapeClass={sc} />
      ))}
    </div>
  );
}

/* ---------- Featured layout (first member large, rest smaller) ---------- */

function FeaturedLayout({ safe, SubH }: { safe: Props; SubH: "h2" | "h3" | "h4" }) {
  const featured = safe.members[0];
  const rest = safe.members.slice(1);
  const sc = IMAGE_SHAPE_CLASSES[safe.photoShape] || IMAGE_SHAPE_CLASSES.circle;

  if (!featured) return null;

  return (
    <div>
      {/* Featured member */}
      <div className="grid grid-cols-1 @md:grid-cols-2 gap-10 @md:gap-16 items-center mb-16">
        <div className="flex justify-center">
          {featured.photoUrl ? (
            <img
              src={featured.photoUrl}
              alt={featured.name}
              className={`w-48 h-48 ${sc} object-cover`}
              loading="lazy"
              data-item-prop="members.0"
            />
          ) : (
            <PhotoPlaceholder name={featured.name} large shapeClass={sc} />
          )}
        </div>
        <div>
          <RichText
            as={SubH}
            html={featured.name}
            className="text-2xl @md:text-3xl font-bold mb-2"
            data-prop="members.0.name"
          />
          <RichText
            as="p"
            html={featured.title}
            className="text-lg opacity-70 mb-4"
            data-prop="members.0.title"
          />
          {safe.showBio && featured.bio && (
            <RichText
              as="p"
              html={featured.bio}
              className="text-base leading-relaxed opacity-80"
              data-prop="members.0.bio"
            />
          )}
        </div>
      </div>

      {/* Remaining members in smaller grid */}
      {rest.length > 0 && (
        <div className="grid grid-cols-2 @md:grid-cols-3 gap-10">
          {rest.map((member, i) => (
            <MemberCard
              key={i}
              member={member}
              index={i + 1}
              showBio={safe.showBio}
              SubH={SubH}
              shapeClass={sc}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);

  if (safe.members.length === 0) return null;

  return (
    <SectionShell globals={safe}>
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80"
              data-prop="subheading"
            />
          )}
        </div>

        {safe.layout === "grid" && <GridLayout safe={safe} SubH={SubH} />}
        {safe.layout === "featured" && <FeaturedLayout safe={safe} SubH={SubH} />}
    </SectionShell>
  );
}
